import { useEffect, useState } from "react";
import { Loader2, ClipboardList, Coins, Package, TrendingUp, Ban } from "lucide-react";
import { api } from "../../lib/api";
import { formatGs } from "../../data/store";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const monthLabel = (ym) => { const [y, m] = ym.split("-"); return `${MONTHS[Number(m) - 1]} ${y}`; };

const Kpi = ({ icon: Icon, label, value, testId, accent = "#0A3977" }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
    <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}14`, color: accent }}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <div className="text-xs font-bold tracking-widest uppercase text-gray-500">{label}</div>
      <div data-testid={testId} className="font-display font-black text-2xl text-gray-900 truncate">{value}</div>
    </div>
  </div>
);

export const SummaryManager = () => {
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.ordersSummary(month).then(setData).finally(() => setLoading(false));
  }, [month]);

  if (loading && !data) return <div className="flex justify-center py-24 text-gray-400"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!data) return null;

  const maxDay = Math.max(1, ...data.daily.map((d) => d.total));
  const maxQty = Math.max(1, ...data.top_products.map((p) => p.qty));

  return (
    <div data-testid="summary-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Resumen de ventas</h1>
          <p className="text-gray-500 text-sm">Pedidos recibidos por WhatsApp (sin contar cancelados).</p>
        </div>
        <select data-testid="summary-month" value={data.month} onChange={(e) => setMonth(e.target.value)}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold outline-none">
          {data.months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={ClipboardList} label="Pedidos del mes" value={data.orders_count} testId="summary-orders" />
        <Kpi icon={Coins} label="Total estimado" value={formatGs(data.total)} testId="summary-total" accent="#E02424" />
        <Kpi icon={TrendingUp} label="Ticket promedio" value={formatGs(data.average)} testId="summary-average" accent="#B45309" />
        <Kpi icon={Package} label="Unidades pedidas" value={data.items_count} testId="summary-items" accent="#15803D" />
      </div>

      <div className="mt-6 grid lg:grid-cols-[1fr_380px] gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-gray-900">Pedidos por día · {monthLabel(data.month)}</h2>
            {data.cancelled_count > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500"><Ban className="w-3.5 h-3.5" /> {data.cancelled_count} cancelados</span>
            )}
          </div>
          {data.daily.length === 0 ? (
            <p data-testid="summary-empty" className="text-gray-400 text-sm py-12 text-center">Todavía no hay pedidos este mes.</p>
          ) : (
            <div className="flex items-end gap-1.5 h-44" data-testid="summary-daily-chart">
              {data.daily.map((d) => (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-1 group" title={`Día ${d.day}: ${d.count} pedidos · ${formatGs(d.total)}`}>
                  <div className="text-[10px] font-bold text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">{d.count}</div>
                  <div className="w-full rounded-t-md bg-[#0A3977] group-hover:bg-[#0067B1] transition-colors" style={{ height: `${Math.max(6, (d.total / maxDay) * 130)}px` }} />
                  <div className="text-[10px] font-mono-code text-gray-400">{d.day}</div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-5 grid grid-cols-4 gap-2 text-center">
            {[["nuevo", "Nuevos", "#1D4ED8"], ["contactado", "Contactados", "#B45309"], ["entregado", "Entregados", "#15803D"], ["cancelado", "Cancelados", "#6B7280"]].map(([k, l, c]) => (
              <div key={k} className="rounded-lg bg-gray-50 py-2">
                <div data-testid={`summary-status-${k}`} className="font-display font-bold text-lg" style={{ color: c }}>{data.by_status[k] || 0}</div>
                <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-display font-bold text-gray-900 mb-4">Productos más pedidos</h2>
          {data.top_products.length === 0 ? (
            <p className="text-gray-400 text-sm py-12 text-center">Sin datos todavía.</p>
          ) : (
            <ol className="space-y-3" data-testid="summary-top-products">
              {data.top_products.map((p, i) => (
                <li key={p.code} data-testid={`summary-top-${p.code}`}>
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <div className="min-w-0 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-[#0A3977] text-white text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                      <span className="font-semibold text-gray-900 truncate">{p.name}</span>
                    </div>
                    <span className="font-bold text-[#E02424] whitespace-nowrap">{p.qty} u.</span>
                  </div>
                  <div className="mt-1 ml-7 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full rounded-full bg-[#F59E0B]" style={{ width: `${(p.qty / maxQty) * 100}%` }} />
                  </div>
                  <div className="ml-7 mt-0.5 text-[11px] text-gray-400 font-mono-code">{p.code} · {formatGs(p.total)}</div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};
